import React, { Suspense, useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { Badge, Button, Icon, Kbd } from "./ui";
import { useTheme, THEMES, ThemeId } from "./stores/useTheme";
import { useCommandBar } from "./stores/useCommandBar";
import ErrorBoundary from "./ErrorBoundary";
import SourcePicker, { SourceKind, sourceToToolPath } from "./components/SourcePicker";
import CommandBar from "./components/CommandBar";
import { createProject } from "./api/projects";

const isMac = typeof navigator !== "undefined" && /mac/i.test(navigator.platform);

const navLinkStyle = ({ isActive }: { isActive: boolean }): React.CSSProperties => ({
  padding: "6px 12px",
  borderRadius: "var(--r-md)",
  fontSize: 13,
  fontWeight: isActive ? 600 : 500,
  textDecoration: "none",
  color: isActive ? "var(--accent)" : "var(--fg-secondary)",
  background: isActive ? "var(--accent-soft)" : "transparent",
  border: `1px solid ${isActive ? "var(--accent-border)" : "transparent"}`,
});

const selectStyle: React.CSSProperties = {
  height: 30,
  padding: "0 8px",
  borderRadius: "var(--r-md)",
  background: "var(--bg-surface)",
  color: "var(--fg-primary)",
  border: "1px solid var(--border-default)",
  fontSize: 12.5,
  fontFamily: "var(--font-sans)",
  cursor: "pointer",
};

const cmdTriggerStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 7,
  width: 240,
  height: 30,
  padding: "0 8px 0 10px",
  borderRadius: "var(--r-md)",
  background: "var(--bg-app)",
  color: "var(--fg-tertiary)",
  border: "1px solid var(--border-default)",
  fontSize: 12.5,
  fontFamily: "var(--font-sans)",
  cursor: "text",
};

/**
 * AppShell — the V0.2 global chrome (top bar + theme switch + account) wrapping
 * a react-router <Outlet>. The project-first landing and 5-region workspace land
 * in Phase B; for now the only route is the legacy tools surface.
 */
export default function AppShell() {
  const navigate = useNavigate();
  const theme = useTheme((s) => s.theme);
  const setTheme = useTheme((s) => s.setTheme);
  const openCommandBar = useCommandBar((s) => s.setOpen);
  const [pickerOpen, setPickerOpen] = useState(false);

  const handlePick = async (kind: SourceKind) => {
    setPickerOpen(false);
    const toolPath = sourceToToolPath(kind);
    if (toolPath) {
      navigate(toolPath);
      return;
    }
    if (kind === "blank") {
      try {
        const p = await createProject("Untitled project");
        navigate(`/p/${p.project_id}`, { state: { project: p } });
      } catch {
        navigate("/");
      }
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "var(--bg-app)",
        color: "var(--fg-primary)",
        fontFamily: "var(--font-sans)",
      }}
    >
      <header
        style={{
          position: "sticky",
          top: 0,
          zIndex: 100,
          display: "flex",
          alignItems: "center",
          gap: 16,
          height: 56,
          padding: "0 20px",
          background: "var(--bg-surface)",
          borderBottom: "1px solid var(--border-subtle)",
        }}
      >
        <div
          onClick={() => navigate("/")}
          style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}
          title="OpenAssetBuilder"
        >
          <img
            src="/oab-logo-final.svg"
            alt="OpenAssetBuilder"
            style={{ height: 38, objectFit: "contain" }}
          />
          <Badge tone="accent" mono>
            V0.2 preview
          </Badge>
        </div>

        <nav style={{ display: "flex", gap: 4, marginLeft: 8 }}>
          <NavLink to="/" end style={navLinkStyle}>
            Projects
          </NavLink>
          <NavLink to="/ledger" style={navLinkStyle}>
            Ledger
          </NavLink>
          <NavLink to="/ramps" style={navLinkStyle}>
            Ramps
          </NavLink>
          <NavLink to="/bridges" style={navLinkStyle}>
            Bridges
          </NavLink>
          <NavLink to="/library" style={navLinkStyle}>
            Library
          </NavLink>
          <NavLink to="/doctor" style={navLinkStyle}>
            Doctor
          </NavLink>
        </nav>

        <div style={{ flex: 1 }} />

        <button
          onClick={() => openCommandBar(true)}
          title="Command palette"
          style={cmdTriggerStyle}
        >
          <Icon name="search" size={14} color="var(--fg-tertiary)" />
          <span style={{ flex: 1, textAlign: "left" }}>Search…</span>
          <Kbd>{isMac ? "⌘" : "Ctrl"}</Kbd>
          <Kbd>K</Kbd>
        </button>

        <Button variant="primary" size="sm" icon="plus" onClick={() => setPickerOpen(true)}>
          New project
        </Button>

        <label
          style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--fg-secondary)" }}
          title="Theme"
        >
          <Icon name={theme === "light" ? "sun" : "moon"} size={15} />
          <select
            value={theme}
            onChange={(e) => setTheme(e.target.value as ThemeId)}
            aria-label="Theme"
            style={selectStyle}
          >
            {THEMES.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
              </option>
            ))}
          </select>
        </label>

        <div
          style={{
            width: 30,
            height: 30,
            borderRadius: "50%",
            background: "var(--accent)",
            color: "var(--fg-on-accent)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          title="Account"
        >
          <Icon name="user" size={16} />
        </div>
      </header>

      <main>
        <ErrorBoundary>
          {/* Routes are React.lazy code-split (App.tsx) so deck.gl/MapLibre/three
              leave the initial bundle; this boundary keeps the chrome visible
              while a route chunk loads. */}
          <Suspense fallback={<div style={{ padding: 40, fontSize: 13, color: "var(--fg-muted)" }}>Loading…</div>}>
            <Outlet />
          </Suspense>
        </ErrorBoundary>
      </main>

      <SourcePicker open={pickerOpen} onClose={() => setPickerOpen(false)} onPick={handlePick} />
      <CommandBar onNewProject={() => setPickerOpen(true)} />
    </div>
  );
}
